import { useParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { ArrowRight, Minus, Plus } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Thumbs, FreeMode } from "swiper/modules";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Oval } from "react-loader-spinner";
import "../components/product/ProductDetails.scss";
import "./ProductSelect.scss";
import "./SingleProductPage.scss";
import "../components/product/OnProductImageText.scss";
import SubscriptionToggle from "../components/cart/SubscriptionToggle";
import ProductPrice from "../components/product/ProductPrice";
import { useGetProductsQuery } from "../store/apiSlice";
import { useAffiliate } from "../hooks/useAffiliate";
import Button from "../components/ui/Button";
import { overlayLogoOnImage } from "../utils/overlayLogoOnImage";
import { useAppNavigate } from "../hooks/useAppNavigate";
import Breadcrumbs from "../components/ui/Breadcrumbs";
import { setCustomizedProduct, setPetType } from "../store/customizerSlice";
import { useDispatch } from "react-redux";


const SingleProductPage = ({
  isMobile,
  withSubscriptionToggle = true
}) => {
  const dispatch = useDispatch();
  const { sku } = useParams();
  const navigate = useAppNavigate();
  const { data: products = [], isLoading: isProductsLoading } = useGetProductsQuery();
  const { affiliate, isLoading: isAffiliateLoading } = useAffiliate();
  const product = useMemo(() => products.find(p => p.SKU === sku), [products, sku]);
  const [allImages, setAllImages] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const breadcrumbs = useMemo(() => {
    if (!product) return [];
    return [
      { label: product.title, link: `/product/${product.sku}` },
    ];
  }, [product]);


  useEffect(() => {
    const prepareImages = async () => {
      if (!product || isAffiliateLoading) return;

      const overrideImages = {
        "SLP-00018": "/product/ShamelessPetsDogTreatsBag1.jpg",
        "SLP-00026": "/product/ShamelessPetsCatTreatsBag1.jpg",
      };

      const additionalImage = overrideImages[product.sku];

      const baseImages = [];
      if (additionalImage) baseImages.push(additionalImage);
      if (product.image) baseImages.push(product.image);

      let finalImages = [...baseImages];

      if (affiliate && product.label) {
        try {
          const updatedGalleryImage = await overlayLogoOnImage(product.label, affiliate.logo);
          finalImages.push(updatedGalleryImage);
        } catch {
          finalImages.push(product.label);
        }
      } else if (product.label) {
        finalImages.push(product.label);
      }

      setAllImages(finalImages);
    };

    prepareImages();
  }, [affiliate, isAffiliateLoading, product?.ID]);


  if (isProductsLoading || !product) {
    return (
      <div className="product-details-page flex justify-center items-center h-screen">
        <Oval height={50} width={50} color="#a99cdf" secondaryColor="#e2e6ff" />
      </div>
    );
  }

  return (
    <div className="product-details-page py-10 full-width">
      <div className="contained-width">
        <Breadcrumbs items={breadcrumbs} />
        <div className="product-details-inner flex flex-col md:flex-row gap-6 mt-10 rounded-20 w-full">

          {isMobile &&
            <div className="flex flex-col gap-3 color-swap">
              <div className="product-tag capitalize">{product.flavor}</div>
              <h4 className="font-normal">{product.title}</h4>
            </div>
          }

          <div className="gallery-container">
            {allImages.length === 0 ? (
              <Oval height={50} width={50} color="#a99cdf" secondaryColor="#e2e6ff" />
            ) : (
              <div className="flex flex-col items-center justify-start gap-4 h-full max-w-full">
                <Swiper
                  modules={[Pagination, Thumbs]}
                  slidesPerView={1}
                  spaceBetween={10}
                  pagination={{ clickable: true, el: ".product-gallery-pagination" }}
                  thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                >
                  {allImages.map((img, index) => (
                    <SwiperSlide key={`main-${index}`}>
                      <div className="rounded-10 border cursor-pointer overflow-hidden" onClick={() => {
                        setLightboxIndex(index);
                        setIsLightboxOpen(true);
                      }}>
                        {(index === 0 && (product.sku !== 'SLP-00018' && product.sku !== 'SLP-00026')) ||
                          (index === 1 && (product.sku === 'SLP-00018' || product.sku === 'SLP-00026'))
                          ?
                          <div className="product-image-overlay">
                            <img src={img} alt={`Product image ${index + 1}`} className="w-full h-full object-cover" />
                            <div className="overlay-text">Personalize With Your Pet's Photo!</div>
                          </div>
                          :
                          <img src={img} alt={`Product image ${index + 1}`} className="w-full object-cover" />
                        }
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                <Swiper
                  onSwiper={setThumbsSwiper}
                  spaceBetween={10}
                  slidesPerView={4}
                  freeMode
                  watchSlidesProgress
                  modules={[FreeMode, Thumbs]}
                  className="thumbs-swiper"
                >
                  {allImages.map((img, index) => (
                    <SwiperSlide key={`thumb-${index}`}>
                      <div className="thumb-slide">
                        <img src={img} alt={`Thumbnail ${index + 1}`} className="rounded border object-cover" />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </div>

          <div className="product-info flex-1 flex flex-col gap-10">
            <div className="flex flex-col gap-3 color-swap">
              {!isMobile &&
                <>
                  <div className="product-tag capitalize">{product.flavor}</div>
                  <h4 className="font-normal">{product.title}</h4>
                </>
              }
              <p className="product-desctiption">{product.description}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-4 color-swap">
                <div className="flex items-center gap-2">
                  <span>Quantity:</span>
                  <div className="quantity-controls flex items-center gap-2 border rounded-lg p-1">
                    <button onClick={() => setQuantity((prev) => Math.max(1, prev - 1))} className="quantity-btn px-2 py-1">
                      <Minus size={14} />
                    </button>
                    <span className="text-lg font-semibold">{quantity}</span>
                    <button onClick={() => setQuantity((prev) => prev + 1)} className="quantity-btn px-2 py-1">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <ProductPrice price={product.price} isSubscribed={isSubscribed} />
              </div>

              {withSubscriptionToggle &&
                <SubscriptionToggle
                  isSubscribed={isSubscribed}
                  onToggle={() => setIsSubscribed((prev) => !prev)}
                  className="mb-4 color-swap"
                />
              }

              <Button
                variant="filled"
                className="justify-between"
                type="button"
                onClick={() => {
                  dispatch(setCustomizedProduct({ product, isSubscribed, quantity }));
                  dispatch(setPetType(product.petType));
                  navigate('/customizer');
                }}
              >
                Start Personalizing
                <ArrowRight size={27} />
              </Button>
            </div>
          </div>
        </div>

        <Lightbox
          open={isLightboxOpen}
          close={() => setIsLightboxOpen(false)}
          slides={allImages.map((img) => ({ src: img }))}
          index={lightboxIndex}
          carousel={{ finite: true }}
        />
      </div>
    </div>
  );
};

export default SingleProductPage;
